import { TeamWith } from "@redotech/account-common/base-team";
import { FinalSaleReturnsDynamicCreditsSettings } from "@redotech/redo-return-settings-common/final-sale-returns-settings";
import { ReturnsSettings } from "@redotech/redo-return-settings-common/returns-settings";
import { groupInput, input, InputSchema, listInput } from "@redotech/ui/form";
import { ValidationError } from "@redotech/ui/validator";
import { arrayEqual, stringEqual } from "@redotech/util/equal";
import round from "lodash/round";
import {
  minMaxValidator,
  pricingDefault,
  pricingFormValues,
  pricingRulesConvertTeamToValue,
  pricingRulesConvertValueToTeam,
} from "./dynamic-pricing";

export const dynamicCreditsTierForm = groupInput({
  /**
   * Absolute return-rate threshold for this bracket, as a percentage string.
   * Empty string on the last row means "unbounded — applies above the
   * previous bracket". Stored on the team as a multiplier of the priced
   * return rate; the form-to-team converter divides this value by the
   * priced return rate at save time.
   */
  thresholdRate: input<string>(),
  /** Percentage 0-100 (decimal in storage). */
  rate: input<string>(),
});

export type DynamicCreditsTierForm = InputSchema.Input<
  typeof dynamicCreditsTierForm
>;
export type DynamicCreditsTierValue = InputSchema.Value<
  typeof dynamicCreditsTierForm
>;

// Index-based keys (matches the `pricingRuleSet` pattern in
// dynamic-pricing.tsx). Indices are stable across re-evaluations of the
// `teamToFormValue` helper; Symbol-based keys would create new identities
// every call and trip the form's dirty detection, blocking navigation via
// the page-form unsaved-changes prompt.
const dynamicCreditsTiersListForm = listInput(
  () => dynamicCreditsTierForm,
  () => ({ thresholdRate: "", rate: "0" }),
  (_, index) => index,
);

export type DynamicCreditsTiersListForm = InputSchema.Input<
  typeof dynamicCreditsTiersListForm
>;

/**
 * Manually declared (rather than inferred from `typeof dynamicCreditsForm`)
 * so that {@link validateDynamicCredits} can be referenced from the form's
 * own validator without creating a TypeScript circular reference.
 */
export interface DynamicCreditsValue {
  enabled: boolean;
  expectedReturnRate: string;
  lookbackWindowDays: string;
  tiers: DynamicCreditsTierValue[];
}

export const dynamicCreditsForm = groupInput(
  {
    enabled: input<boolean>(),
    /** Percentage 0-100 in the form, decimal 0-1 in storage. */
    expectedReturnRate: input<string>(),
    /** Integer days. */
    lookbackWindowDays: input<string>(),
    tiers: dynamicCreditsTiersListForm,
  },
  {
    validator: (value: DynamicCreditsValue) =>
      validateDynamicCredits(value).errors,
  },
);

export type DynamicCreditsForm = InputSchema.Input<typeof dynamicCreditsForm>;

export interface DynamicCreditsTierFieldErrors {
  /** null = valid, string = error message to render under the input. */
  thresholdRate: string | null;
  rate: string | null;
}

export interface DynamicCreditsFieldErrors {
  expectedReturnRate: string | null;
  lookbackWindowDays: string | null;
  /** Form-wide errors not bound to a specific input (e.g. "Add at least one bracket"). */
  form: string | null;
  tiers: DynamicCreditsTierFieldErrors[];
}

export interface DynamicCreditsValidation {
  errors: ValidationError[];
  fieldErrors: DynamicCreditsFieldErrors;
}

/**
 * Validates dynamic-credits form values. Returns both the flat error list
 * (consumed by the form's `validator` so `input.allErrors` keeps working)
 * and a per-field error map (consumed by the React component to render the
 * specific message inline beneath each invalid input). Single source of
 * truth so the highlights and error copy never drift.
 */
export function validateDynamicCredits(
  value: DynamicCreditsValue,
): DynamicCreditsValidation {
  const fieldErrors: DynamicCreditsFieldErrors = {
    expectedReturnRate: null,
    lookbackWindowDays: null,
    form: null,
    tiers: value.tiers.map(() => ({ thresholdRate: null, rate: null })),
  };
  if (!value.enabled) {
    return { errors: [], fieldErrors };
  }
  const errors: ValidationError[] = [];
  const recordField = (
    field: "expectedReturnRate" | "lookbackWindowDays",
    message: string,
  ) => {
    fieldErrors[field] = message;
    errors.push(message);
  };
  const recordTierField = (
    index: number,
    field: "thresholdRate" | "rate",
    message: string,
  ) => {
    fieldErrors.tiers[index]![field] = message;
    errors.push(`Bracket ${index + 1}: ${message}`);
  };

  const expected = Number(value.expectedReturnRate);
  if (
    value.expectedReturnRate.trim() === "" ||
    Number.isNaN(expected) ||
    expected <= 0 ||
    expected > 100
  ) {
    recordField(
      "expectedReturnRate",
      "Must be a percentage between 0% and 100%",
    );
  }

  // lookbackWindowDays is optional — when blank the invoice-time hook
  // short-circuits, which is the documented "disabled" path. We only flag a
  // value that's present but malformed. Minimum window is 21 days so that a
  // cohort of orders has had a meaningful chance to surface returns before
  // we true up against it.
  if (value.lookbackWindowDays.trim() !== "") {
    const lookback = Number(value.lookbackWindowDays);
    if (!Number.isInteger(lookback) || lookback < 21) {
      recordField(
        "lookbackWindowDays",
        "Must be a whole number of days (≥ 21)",
      );
    }
  }

  if (value.tiers.length === 0) {
    fieldErrors.form = "Add at least one bracket";
    errors.push("Add at least one bracket");
    return { errors, fieldErrors };
  }

  let prevThreshold = 0;
  let prevRate = Infinity;
  for (const [index, tier] of value.tiers.entries()) {
    const isLast = index === value.tiers.length - 1;

    const rate = Number(tier.rate);
    if (
      tier.rate.trim() === "" ||
      Number.isNaN(rate) ||
      rate < 0 ||
      rate > 100
    ) {
      recordTierField(index, "rate", "Rate must be between 0% and 100%");
    } else if (rate > prevRate) {
      recordTierField(
        index,
        "rate",
        "Rate cannot exceed the previous bracket's rate",
      );
    } else {
      prevRate = rate;
    }

    if (tier.thresholdRate.trim() === "") {
      if (!isLast) {
        recordTierField(
          index,
          "thresholdRate",
          "Only the last bracket may be unbounded",
        );
      }
    } else {
      const threshold = Number(tier.thresholdRate);
      if (Number.isNaN(threshold) || threshold <= 0 || threshold > 100) {
        recordTierField(
          index,
          "thresholdRate",
          "Threshold must be a percentage greater than 0%",
        );
      } else if (threshold <= prevThreshold) {
        recordTierField(
          index,
          "thresholdRate",
          `Threshold must be greater than ${prevThreshold}%`,
        );
      } else {
        prevThreshold = threshold;
      }
    }
  }

  return { errors, fieldErrors };
}

// Default to 5 empty rows so the merchant team can paste a 2-column grid
// (threshold, credit rate) generated by the pricing tool. No starter values
// — every cell is filled in from the paste or by hand.
const DEFAULT_DYNAMIC_CREDITS_TIERS: DynamicCreditsTierValue[] = [
  { thresholdRate: "", rate: "" },
  { thresholdRate: "", rate: "" },
  { thresholdRate: "", rate: "" },
  { thresholdRate: "", rate: "" },
  { thresholdRate: "", rate: "" },
];

export const dynamicCreditsDefault: DynamicCreditsValue = {
  enabled: false,
  expectedReturnRate: "",
  lookbackWindowDays: "",
  tiers: DEFAULT_DYNAMIC_CREDITS_TIERS,
};

export const finalSaleReturnsForm = groupInput(
  {
    enabled: input<boolean>(),
    /**
     * @deprecated Use validCollectionTitles instead. All of the other collection models use the title,
     * not the handle, so we need to store both. After the migration, we can remove this field.
     */
    validCollections: input<string[]>({ equal: arrayEqual(stringEqual) }),
    validCollectionTitles: input<string[]>({ equal: arrayEqual(stringEqual) }),
    validProductTags: input<string[]>({ equal: arrayEqual(stringEqual) }),
    reimbursementPercentage: input<string>(),
    shippingLabelCoveragePrice: input<string>(),
    finalSaleRevSharePercentage: input<string>(),
    allRedoPurchasesAreFinalSale: input<boolean>(),
    allCovered: input<boolean>(),
    allEligible: input<boolean>(),
    eligibleDiscountThreshold: input<string>(),
    dynamicCredits: dynamicCreditsForm,
    ...pricingFormValues,
  },
  { validator: minMaxValidator },
);

export type FinalSaleReturnsForm = InputSchema.Input<
  typeof finalSaleReturnsForm
>;

export type FinalSaleReturnsValue = InputSchema.Value<
  typeof finalSaleReturnsForm
>;

export function finalSaleReturnsTeamToFormValue(
  team: TeamWith<{ returns?: ReturnsSettings; countries: string[] }>,
): FinalSaleReturnsValue {
  return {
    // General
    enabled: team.settings.returns?.finalSaleReturns?.enabled || false,
    validCollections:
      team.settings.returns?.finalSaleReturns?.validCollections || [],
    validCollectionTitles:
      team.settings.returns?.finalSaleReturns?.validCollectionTitles || [],
    validProductTags:
      team.settings.returns?.finalSaleReturns?.validProductTags || [],
    minPrice:
      team.settings.returns?.finalSaleReturns?.minPrice?.toString() || "1",
    maxPrice:
      team.settings.returns?.finalSaleReturns?.maxPrice?.toString() || "15",
    percentage: team.settings.returns?.finalSaleReturns?.percentage
      ? String(
          round(team.settings.returns.finalSaleReturns.percentage * 100.0, 3),
        )
      : "2",
    reimbursementPercentage: team.settings.returns?.finalSaleReturns
      ?.reimbursementPercentage
      ? String(
          round(
            team.settings.returns.finalSaleReturns.reimbursementPercentage *
              100.0,
            3,
          ),
        )
      : "100",
    // Use pricePerOrder for returns coverage as the default
    shippingLabelCoveragePrice:
      team.settings.returns?.finalSaleReturns?.shippingLabelCoveragePrice?.toString() ||
      team.pricePerOrder ||
      "",
    finalSaleRevSharePercentage: team.settings.returns?.finalSaleReturns
      ?.finalSaleRevSharePercentage
      ? String(
          team.settings.returns.finalSaleReturns.finalSaleRevSharePercentage,
        )
      : "0",
    pricingRuleSet: pricingRulesConvertTeamToValue(
      team.settings.returns?.finalSaleReturns?.pricingRuleSet,
      team.settings.countries,
      team.settings.returns?.finalSaleReturns?.percentage,
    ),
    internationalPricingEnabled: team.settings.returns?.finalSaleReturns
      ?.pricingRuleSet?.length
      ? team.settings.returns?.finalSaleReturns?.pricingRuleSet?.length > 1
      : false,
    allRedoPurchasesAreFinalSale:
      team.settings.returns?.finalSaleReturns?.allRedoPurchasesAreFinalSale ??
      false,
    allCovered: team.settings.returns?.finalSaleReturns?.allCovered ?? false,
    allEligible: team.settings.returns?.finalSaleReturns?.allEligible ?? false,
    eligibleDiscountThreshold:
      team.settings.returns?.finalSaleReturns?.eligibleDiscountThreshold != null
        ? String(
            team.settings.returns.finalSaleReturns.eligibleDiscountThreshold,
          )
        : "",
    dynamicCredits: dynamicCreditsTeamToFormValue(
      team.settings.returns?.finalSaleReturns?.dynamicCredits,
    ),
  };
}

export function dynamicCreditsTeamToFormValue(
  dynamicCredits: FinalSaleReturnsDynamicCreditsSettings | null | undefined,
): DynamicCreditsValue {
  if (!dynamicCredits) {
    return dynamicCreditsDefault;
  }
  // Schema stores `tierMultiplier` as a multiple of `expectedReturnRate`
  // (decimal 0-1). The form displays each bracket's threshold as an
  // absolute percentage so CSMs don't have to do the arithmetic in their
  // heads — multiply the multiplier by the priced rate (in %) here. When
  // priced rate is missing we fall back to the form default (1%) so we
  // can still render something sensible; the form-to-team converter will
  // refuse to save if priced rate is missing.
  const pricedReturnRatePct =
    dynamicCredits.expectedReturnRate != null
      ? dynamicCredits.expectedReturnRate * 100
      : 0;
  const tiers: DynamicCreditsTierValue[] =
    dynamicCredits.tiers && dynamicCredits.tiers.length > 0
      ? dynamicCredits.tiers.map((tier) => ({
          thresholdRate:
            tier.tierMultiplier != null && pricedReturnRatePct > 0
              ? String(round(tier.tierMultiplier * pricedReturnRatePct, 3))
              : "",
          rate: String(round(tier.rate * 100, 3)),
        }))
      : dynamicCreditsDefault.tiers;
  return {
    enabled: dynamicCredits.enabled ?? false,
    expectedReturnRate:
      dynamicCredits.expectedReturnRate != null
        ? String(round(pricedReturnRatePct, 3))
        : "",
    lookbackWindowDays:
      dynamicCredits.lookbackWindowDays != null
        ? String(dynamicCredits.lookbackWindowDays)
        : "",
    tiers,
  };
}

export function finalSaleReturnsFormValueToTeam(
  finalSaleReturns: FinalSaleReturnsValue,
) {
  return {
    enabled: finalSaleReturns.enabled,
    validCollections: finalSaleReturns.validCollections,
    validCollectionTitles: finalSaleReturns.validCollectionTitles,
    validProductTags: finalSaleReturns.validProductTags,
    minPrice: Number(finalSaleReturns.minPrice),
    maxPrice: Number(finalSaleReturns.maxPrice),
    percentage: round(+finalSaleReturns.percentage, 3) / 100.0,
    reimbursementPercentage:
      round(+finalSaleReturns.reimbursementPercentage, 3) / 100.0,
    shippingLabelCoveragePrice: finalSaleReturns.shippingLabelCoveragePrice
      ? Number(finalSaleReturns.shippingLabelCoveragePrice)
      : undefined,
    finalSaleRevSharePercentage: Number(
      finalSaleReturns.finalSaleRevSharePercentage,
    ),
    allRedoPurchasesAreFinalSale: finalSaleReturns.allRedoPurchasesAreFinalSale,
    allCovered: finalSaleReturns.allCovered,
    allEligible: finalSaleReturns.allEligible,
    eligibleDiscountThreshold:
      finalSaleReturns.eligibleDiscountThreshold === ""
        ? null
        : Number(finalSaleReturns.eligibleDiscountThreshold),
    pricingRuleSet: pricingRulesConvertValueToTeam(
      finalSaleReturns.pricingRuleSet,
    ),
    dynamicCredits: dynamicCreditsFormValueToTeam(
      finalSaleReturns.dynamicCredits,
    ),
  };
}

export function dynamicCreditsFormValueToTeam(
  value: DynamicCreditsValue,
): FinalSaleReturnsDynamicCreditsSettings {
  // Form stores each bracket's threshold as an absolute return-rate
  // percentage; schema stores it as a multiplier of `expectedReturnRate`.
  // Convert at save time: multiplier = thresholdRate% / pricedRate%.
  const pricedRatePct =
    value.expectedReturnRate.trim() === ""
      ? null
      : Number(value.expectedReturnRate);
  return {
    enabled: value.enabled,
    expectedReturnRate:
      pricedRatePct == null ? null : round(pricedRatePct, 3) / 100.0,
    lookbackWindowDays:
      value.lookbackWindowDays.trim() === ""
        ? null
        : Number(value.lookbackWindowDays),
    tiers: value.tiers.map((tier) => ({
      tierMultiplier:
        tier.thresholdRate.trim() === "" ||
        pricedRatePct == null ||
        pricedRatePct <= 0
          ? null
          : round(Number(tier.thresholdRate) / pricedRatePct, 4),
      rate: round(Number(tier.rate), 3) / 100.0,
    })),
  };
}

export const finalSaleReturnsDefault: FinalSaleReturnsValue = {
  enabled: false,
  reimbursementPercentage: "100",
  shippingLabelCoveragePrice: "",
  finalSaleRevSharePercentage: "0",
  allRedoPurchasesAreFinalSale: false,
  allCovered: false,
  allEligible: false,
  eligibleDiscountThreshold: "",
  validCollections: [] as string[],
  validCollectionTitles: [] as string[],
  validProductTags: [] as string[],
  dynamicCredits: dynamicCreditsDefault,
  ...pricingDefault,
};
